import { PersonaConditionLeaf, PersonaMetricBrief } from '@/common/api/params/persona'

import Text, { TextColor } from '@/components/view/atoms/text/Text'

import { EDITABLE_OPS, OP_LABELS } from '@/components/container/modules/persona/conditionLeaves'

/**
 * 判定条件の閾値を編集するフォーム。
 *
 * **サイト側（site_persona）と全体管理（persona_template）で共用する。**
 * 同じ条件式を別々の画面で編集できる状態を作ると、片方だけ直したときに
 * 表現できる式が食い違う（実際に src や演算子の扱いで差が出る）。
 *
 * 触るのは**値と演算子だけ**。メトリクス（何を測るか）は増減させない。
 * 測り方をここから変えられるようにすると横断比較が成立しなくなる。
 */

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const ConditionLeafEditor = ({
  leaves,
  metrics,
  onChange
}: {
  leaves: PersonaConditionLeaf[]
  metrics: PersonaMetricBrief[]
  onChange: (next: PersonaConditionLeaf[]) => void
}) => {
  const typeOf = (key: string) =>
    metrics.find((m) => m.metric_key === key)?.value_type ?? 'numeric'

  return (
    <>
      {leaves.map((leaf, i) => {
        const bool = typeOf(leaf.metric) === 'bool'
        const meta = metrics.find((m) => m.metric_key === leaf.metric)
        const update = (patch: Partial<PersonaConditionLeaf>) =>
          onChange(leaves.map((l, j) => (i === j ? { ...l, ...patch } : l)))
        return (
          <div key={`${leaf.metric}:${leaf.src ?? 'raw'}`} className='flex flex-col gap-1'>
            <Text size={12}>
              {meta?.description ?? leaf.metric}
              {leaf.src === 'pct' ? '（サイト内の順位）' : ''}
            </Text>
            <Text size={10} color={TextColor.TERTIARY}>
              {leaf.metric}
              {meta?.window_days ? ` ・ 直近${meta.window_days}日` : ''}
              {leaf.src === 'pct' ? ' ・ 0〜100の百分位で指定（90＝上位10％）' : ''}
            </Text>
            <div className='flex flex-wrap items-center gap-2'>
              {bool ? (
                <select
                  className={textInput}
                  value={String(leaf.value === true)}
                  onChange={(e) => update({ value: e.target.value === 'true' })}
                >
                  <option value='true'>該当する</option>
                  <option value='false'>該当しない</option>
                </select>
              ) : (
                <>
                  <input
                    className={`${textInput} w-32`}
                    type='number'
                    step='any'
                    value={leaf.value === undefined || leaf.value === null ? '' : String(leaf.value)}
                    onChange={(e) =>
                      update({ value: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                  />
                  <select
                    className={textInput}
                    value={leaf.op}
                    onChange={(e) => update({ op: e.target.value })}
                  >
                    {EDITABLE_OPS.map((op) => (
                      <option key={op} value={op}>
                        {OP_LABELS[op]}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
        )
      })}
    </>
  )
}

export default ConditionLeafEditor
